import { yupResolver } from '@hookform/resolvers/yup';
import MobileNumberInput from 'components/_common/MobileNumberInput';
import MultilineFieldError from 'components/UI/MultilineFieldError';
import { DEFAULT_MOBILE_CODE, MOBILE_CODE_OPTION_LIST } from 'helpers/DataHelper';
import {
  emailREGEX,
  FFLNumberRules,
  mobileNumberREGEX,
  passwordRules,
} from 'helpers/ValidationHelper';
import { t } from 'i18next';
import FFLNumberInput from 'modules/admin/components/_common/ModuleSections/FFLNumberInput';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { executeFFLSignUp } from 'store/auth/actions/AuthAction';
import * as Yup from 'yup';

const EyeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: '#9b9daf', cursor: 'pointer' }}
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: '#9b9daf', cursor: 'pointer' }}
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

interface FFLSignUpFormSectionProps {
  onClose: () => void;
  onSwitchToLogin: () => void;
}

const defaultValue = {
  ffl_no: '',
  ffl_expiry_date: '',
  business_name: '',
  first_name: '',
  last_name: '',
  mobile_code: DEFAULT_MOBILE_CODE,
  mobile: '',
  personal_mobile_code: DEFAULT_MOBILE_CODE,
  personal_mobile: '',
  email: '',
  password: '',
  confirm_password: '',
  expiration_date: '',
};

const FFLSignUpFormSection: React.FC<FFLSignUpFormSectionProps> = ({
  onClose,
  onSwitchToLogin,
}) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({ new: false, confirm: false });

  const validationSchema = Yup.object().shape({
    ffl_no: FFLNumberRules('ffl_no'),
    ffl_expiry_date: Yup.string().nullable(),
    business_name: Yup.string().required(
      t('required_error_msg', { field_name: t('business_name') })
    ),
    first_name: Yup.string().required(t('required_error_msg', { field_name: t('first_name') })),
    last_name: Yup.string().required(t('required_error_msg', { field_name: t('last_name') })),
    mobile_code: Yup.string().nullable(),
    mobile: Yup.string()
      .required(t('required_error_msg', { field_name: t('phone_number') }))
      .matches(mobileNumberREGEX, t('invalid_error_msg', { field_name: t('phone_number') })),
    personal_mobile_code: Yup.string().nullable(),
    personal_mobile: Yup.string()
      .nullable()
      .test(
        'is-valid-number',
        t('invalid_error_msg', { field_name: t('personal_number') }),
        function (value) {
          if (!value || !value.trim()) {
            return true;
          }
          return mobileNumberREGEX.test(value);
        }
      ),
    email: Yup.string()
      .required(t('required_error_msg', { field_name: t('email_address') }))
      .matches(emailREGEX, t('invalid_error_msg', { field_name: t('email_address') })),
    password: passwordRules(),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('password')], t('password_not_match'))
      .required(t('required_error_msg', { field_name: t('confirm_password') })),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValue,
  });

  const onSubmit = async (data: any) => {
    await executeFFLSignUp(data, setLoading, onClose);
  };

  return (
    <form className="login-modal-content__form" onSubmit={handleSubmit(onSubmit)}>
      <div className="form-heading-section">
        <h3>{t('welcome_back')}</h3>
        <p>{t('login_to_buy_and_manage')}</p>
      </div>

      {/* FFL Number */}
      <div className="input-group-custom">
        <label>
          {t('ffl_no')} <span className="text-danger">*</span>
        </label>
        <Controller
          name="ffl_no"
          control={control}
          render={({ field }) => (
            <FFLNumberInput
              value={field.value}
              onChange={field.onChange}
              className={errors.ffl_no ? 'input-error' : ''}
            />
          )}
        />
        {errors.ffl_no && <span className="error-message">{errors.ffl_no.message}</span>}
      </div>

      {/* Business Name */}
      <div className="input-group-custom">
        <label htmlFor="modal-ffl-business-name">
          {t('business_name')} <span className="text-danger">*</span>
        </label>
        <input
          id="modal-ffl-business-name"
          type="text"
          {...register('business_name')}
          placeholder={t('business_name')}
          className={errors.business_name ? 'input-error' : ''}
        />
        {errors.business_name && (
          <span className="error-message">{errors.business_name.message}</span>
        )}
      </div>

      {/* First Name & Last Name */}
      <div className="form-row-2col">
        <div className="input-group-custom">
          <label htmlFor="modal-ffl-firstname">
            {t('first_name')} <span className="text-danger">*</span>
          </label>
          <input
            id="modal-ffl-firstname"
            type="text"
            {...register('first_name')}
            placeholder={t('first_name')}
            className={errors.first_name ? 'input-error' : ''}
          />
          {errors.first_name && <span className="error-message">{errors.first_name.message}</span>}
        </div>

        <div className="input-group-custom">
          <label htmlFor="modal-ffl-lastname">
            {t('last_name')} <span className="text-danger">*</span>
          </label>
          <input
            id="modal-ffl-lastname"
            type="text"
            {...register('last_name')}
            placeholder={t('last_name')}
            className={errors.last_name ? 'input-error' : ''}
          />
          {errors.last_name && <span className="error-message">{errors.last_name.message}</span>}
        </div>
      </div>

      {/* Phone Number */}
      <div className="input-group-custom">
        <label htmlFor="modal-ffl-phone">
          {t('phone_number')} <span className="text-danger">*</span>
        </label>
        <div className="phone-select-input">
          <select {...register('mobile_code')} className="phone-code-select">
            {MOBILE_CODE_OPTION_LIST.map((opt) => (
              <option key={opt.code} value={`${opt.code}`}>
                {opt.label}
              </option>
            ))}
          </select>
          <MobileNumberInput
            plain
            control={control}
            name="mobile"
            id="modal-ffl-phone"
            placeholder={t('phone_number')}
            className={errors.mobile ? 'input-error' : ''}
          />
        </div>
        {errors.mobile && <span className="error-message">{errors.mobile.message}</span>}
      </div>

      {/* Personal Number */}
      <div className="input-group-custom">
        <label htmlFor="modal-ffl-personal-phone">{t('personal_number')}</label>
        <div className="phone-select-input">
          <select {...register('personal_mobile_code')} className="phone-code-select">
            {MOBILE_CODE_OPTION_LIST.map((opt) => (
              <option key={opt.code} value={`${opt.code}`}>
                {opt.label}
              </option>
            ))}
          </select>
          <MobileNumberInput
            plain
            control={control}
            name="personal_mobile"
            id="modal-ffl-personal-phone"
            placeholder={t('personal_number')}
            className={errors.personal_mobile ? 'input-error' : ''}
          />
        </div>
        {errors.personal_mobile && (
          <span className="error-message">{errors.personal_mobile.message}</span>
        )}
      </div>

      {/* Email Address */}
      <div className="input-group-custom">
        <label htmlFor="modal-ffl-email">
          {t('email_address')} <span className="text-danger">*</span>
        </label>
        <input
          id="modal-ffl-email"
          type="email"
          {...register('email')}
          autoComplete="off"
          placeholder={t('email_address')}
          className={errors.email ? 'input-error' : ''}
        />
        {errors.email && <span className="error-message">{errors.email.message}</span>}
      </div>

      {/* Password */}
      <div className="input-group-custom">
        <label htmlFor="modal-ffl-password">
          {t('password')} <span className="text-danger">*</span>
        </label>
        <div className="password-input-wrapper">
          <input
            id="modal-ffl-password"
            type={showPassword.new ? 'text' : 'password'}
            {...register('password')}
            autoComplete="new-password"
            placeholder={t('password')}
            className={errors.password ? 'input-error' : ''}
          />
          <span
            className="password-toggle-icon"
            onClick={() => setShowPassword((prev) => ({ ...prev, new: !prev.new }))}
          >
            {showPassword.new ? <EyeSlashIcon /> : <EyeIcon />}
          </span>
        </div>
        {errors.password && (
          <span className="error-message">
            <MultilineFieldError message={errors.password.message as string} />
          </span>
        )}
      </div>

      {/* Confirm Password */}
      <div className="input-group-custom">
        <label htmlFor="modal-ffl-confirm-password">
          {t('confirm_password')} <span className="text-danger">*</span>
        </label>
        <div className="password-input-wrapper">
          <input
            id="modal-ffl-confirm-password"
            type={showPassword.confirm ? 'text' : 'password'}
            {...register('confirm_password')}
            autoComplete="new-password"
            placeholder={t('confirm_password')}
            className={errors.confirm_password ? 'input-error' : ''}
          />
          <span
            className="password-toggle-icon"
            onClick={() => setShowPassword((prev) => ({ ...prev, confirm: !prev.confirm }))}
          >
            {showPassword.confirm ? <EyeSlashIcon /> : <EyeIcon />}
          </span>
        </div>
        {errors.confirm_password && (
          <span className="error-message">{errors.confirm_password.message}</span>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className="btn btn-orange login-modal-content__submit-btn"
        disabled={loading}
      >
        {loading ? t('loading') : t('sign_up')}
      </button>

      {/* Already have an account prompt */}
      <div className="form-signup-prompt">
        <p>
          Already have an account?{' '}
          <span onClick={onSwitchToLogin} className="orange-link" style={{ cursor: 'pointer' }}>
            {t('login')}
          </span>
        </p>
      </div>
    </form>
  );
};

export default FFLSignUpFormSection;
